/**
 * 소유자 인증 날짜 유틸
 *
 * BE 반환 포맷: "YYYY-MM-DD HH:MM:SS" 또는 "YYYY-MM-DD"
 * FE 표시 포맷: "YYYY.MM.DD" 또는 "YYYY.MM.DD HH:MM"
 */

/**
 * 날짜 문자열을 Date 객체로 안전하게 파싱
 * "YYYY-MM-DD HH:MM:SS", "YYYY-MM-DD", "YYYY.MM.DD" 모두 지원
 * @param {string|Date} str
 * @returns {Date|null}
 */
export function parseDate(str) {
  if (!str) return null;
  if (str instanceof Date) return isNaN(str) ? null : str;
  const normalized = String(str).replace(/\./g, '-');
  const d = new Date(normalized);
  return isNaN(d) ? null : d;
}

/**
 * "YYYY.MM.DD" 형식으로 포맷
 * @param {string|Date} str
 * @returns {string}
 */
export function formatDate(str) {
  const d = parseDate(str);
  if (!d) return '-';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}.${mm}.${dd}`;
}

/**
 * "YYYY.MM.DD HH:MM" 형식으로 포맷
 * @param {string|Date} str
 * @returns {string}
 */
export function formatDateTime(str) {
  const d = parseDate(str);
  if (!d) return '-';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}.${mm}.${dd} ${hh}:${mi}`;
}

/**
 * 두 날짜 간 일수 차이 (to - from), 시간 무시
 * @param {string|Date} fromStr
 * @param {string|Date} toStr
 * @returns {number|null}
 */
export function diffDays(fromStr, toStr) {
  const from = parseDate(fromStr);
  const to = parseDate(toStr);
  if (!from || !to) return null;
  from.setHours(0, 0, 0, 0);
  to.setHours(0, 0, 0, 0);
  return Math.ceil((to - from) / (1000 * 60 * 60 * 24));
}

/**
 * D-day 문자열 반환
 * @param {string|Date} targetStr  대상 날짜
 * @param {string|Date} [baseStr]  기준 날짜 (생략 시 오늘)
 * @returns {string} "D-7", "D-0", "만료"
 */
export function formatDday(targetStr, baseStr) {
  const base = baseStr ? parseDate(baseStr) : new Date();
  const diff = diffDays(base, targetStr);
  if (diff === null) return '-';
  return diff >= 0 ? `D-${diff}` : '만료';
}

/**
 * 만료 여부 판단
 * @param {string|Date} deadlineStr  만료 날짜
 * @param {string|Date} [baseStr]    기준 날짜 (생략 시 오늘)
 * @returns {boolean}
 */
export function isExpired(deadlineStr, baseStr) {
  const base = baseStr ? parseDate(baseStr) : new Date();
  const diff = diffDays(base, deadlineStr);
  if (diff === null) return false;
  return diff < 0;
}

/**
 * 날짜에 N일을 더한 결과를 "YYYY.MM.DD" 로 반환
 * @param {string|Date} dateStr
 * @param {number} days
 * @returns {string}
 */
export function addDays(dateStr, days) {
  const d = parseDate(dateStr);
  if (!d) return '-';
  d.setDate(d.getDate() + days);
  return formatDate(d);
}

/**
 * 오늘 날짜를 "YYYY.MM.DD" 로 반환
 * @returns {string}
 */
export function today() {
  return formatDate(new Date());
}
